import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HardHat, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1768578717759-4f4cc8e58310?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwc3Vuc2V0fGVufDB8fHx8MTc4NjE4MjUwMXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(user.role === "magazzino_alluminio" ? "/gestionale/magazzino-alluminio" : "/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, payload);
      setUser(data);
      navigate(data.role === "magazzino_alluminio" ? "/gestionale/magazzino-alluminio" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center">
              <HardHat className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <p className="font-heading font-extrabold text-xl leading-none">CantierePro</p>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mt-1">Gestione Edile</p>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold">
            {mode === "login" ? "Bentornato." : "Crea account."}
          </h1>
          <p className="text-muted-foreground mt-3 mb-8">
            {mode === "login"
              ? "Accedi per gestire i tuoi cantieri, manodopera e materiali."
              : "Registrati per iniziare a gestire i tuoi cantieri."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input id="name" data-testid="name-input" value={name}
                  onChange={(e) => setName(e.target.value)} required placeholder="Mario Rossi" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required placeholder="nome@azienda.it" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>

            {error && (
              <p className="text-sm text-destructive font-medium" data-testid="auth-error">{error}</p>
            )}

            <Button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full h-11 rounded-md font-bold text-base transition-transform active:scale-[0.98]">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Accedi" : "Registrati"}
            </Button>
          </form>

          <p className="hidden">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Cantiere" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-secondary/70" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold tracking-tight max-w-md">
            Ogni cantiere sotto controllo.
          </h2>
          <p className="mt-4 text-white/80 max-w-md text-lg">
            Manodopera, materiali e rendicontazione a una data precisa. Tutto in un unico posto.
          </p>
        </div>
      </div>
    </div>
  );
}

